/* eslint-disable camelcase */
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { errorParserRequest } from '../../../../utils/errorParser'
import { TOAST_DEFAULT_CONFIG } from '../../../../constants/toast'
import {
  dispose,
  getPageTargetingDataAction,
  patchPageTargetingDataAction,
  selectIsPageTargetingDataLoading,
  selectIsPageTargetingItemsUpdating,
  selectPageTargetingAdditionalInfo,
  selectPageTargetingData,
  setAdditionalInfo,
  updatePageTargetingData,
} from '../../../../store/slice/pageTargeting'
import { getAccountID } from '../../../../utils/auth'

export const usePageTargeting = ({ productData, setFooterHeight }) => {
  const {
    targetingType_CATEGORY,
    targetingType_CATEGORY_init,
    targetingType_PDP,
    targetingType_PDP_init,
    targetingType_SEARCH,
    targetingType_SEARCH_init,
    targetingType_PRODUCT,
    targetingType_PRODUCT_init,
  } = useSelector(selectPageTargetingData)
  const isDataFetching = useSelector(selectIsPageTargetingDataLoading)
  const isDataUpdating = useSelector(selectIsPageTargetingItemsUpdating)
  const { channelID, productID, accountID } = useSelector(selectPageTargetingAdditionalInfo)
  const dispatch = useDispatch()

  const footerRef = useRef(null)

  const isPDPEnable = useMemo(() => typeof targetingType_PDP_init !== 'undefined', [targetingType_PDP_init])
  const isProductEnable = useMemo(() => typeof targetingType_PRODUCT_init !== 'undefined', [targetingType_PRODUCT_init])
  const isChangesExist = useMemo(
    () =>
      targetingType_CATEGORY !== targetingType_CATEGORY_init ||
      targetingType_PDP !== targetingType_PDP_init ||
      targetingType_SEARCH !== targetingType_SEARCH_init ||
      targetingType_PRODUCT !== targetingType_PRODUCT_init,
    [
      targetingType_CATEGORY,
      targetingType_CATEGORY_init,
      targetingType_PDP,
      targetingType_PDP_init,
      targetingType_SEARCH,
      targetingType_SEARCH_init,
      targetingType_PRODUCT,
      targetingType_PRODUCT_init,
    ]
  )

  const showErrorMsg = useMemo(() => {
    if (isProductEnable) {
      return !targetingType_CATEGORY && !targetingType_PDP && !targetingType_SEARCH && !targetingType_PRODUCT
    }
    return !targetingType_CATEGORY && !targetingType_PDP && !targetingType_SEARCH
  }, [targetingType_CATEGORY, targetingType_PDP, targetingType_SEARCH, targetingType_PRODUCT, isProductEnable])

  const onUpdate = (data) => {
    dispatch(updatePageTargetingData(data))
  }

  const saveChanges = useCallback(() => {
    dispatch(
      patchPageTargetingDataAction({
        channelID,
        productID,
        accountID,
        data: {
          targetingType_CATEGORY,
          targetingType_PDP,
          targetingType_SEARCH,
          targetingType_PRODUCT,
        },
      })
    )
      .unwrap()
      .catch((err) => {
        toast.error(errorParserRequest(err), TOAST_DEFAULT_CONFIG)
      })
  }, [
    channelID,
    productID,
    accountID,
    targetingType_CATEGORY,
    targetingType_PDP,
    targetingType_SEARCH,
    targetingType_PRODUCT,
  ])

  useEffect(() => {
    if (channelID && productID && accountID) {
      dispatch(
        getPageTargetingDataAction({
          accountID,
          channelID,
          productID,
        })
      )
    }
  }, [channelID, productID, accountID])

  useEffect(() => {
    dispatch(
      setAdditionalInfo({
        accountID: getAccountID(),
        channelID: productData.channelID,
        productID: productData.productID,
        isBol: true,
      })
    )
  }, [productData])

  useEffect(() => {
    if (!footerRef.current) return
    setFooterHeight(footerRef.current.scrollHeight)
  }, [])

  useEffect(
    () => () => {
      dispatch(dispose())
    },
    []
  )

  return {
    targetingType_CATEGORY,
    targetingType_PDP,
    targetingType_SEARCH,
    targetingType_PRODUCT,
    isPDPEnable,
    isProductEnable,
    isDataFetching,
    isDataUpdating,
    saveChanges,
    onUpdate,
    accountID,
    channelID,
    productID,
    footerRef,
    isChangesExist,
    showErrorMsg,
  }
}
